import Alert from '@/components/Alert/Alert'
import { HStack } from '@/components/ui/hstack'
import { Pressable } from '@/components/ui/pressable'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import useKeyboardState from '@/lib/hooks/useKeyboardState'
import usePreventLeaveWeb from '@/lib/hooks/usePreventLeaveWeb'
import { selectConversationStarted, setUserInputContent } from '@/redux/slices/aiChatSlice'
import { selectLatestGeneratedForm, setNavigatedAway } from '@/redux/slices/formAIGenerationSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useHeaderHeight } from '@react-navigation/elements'
import { usePreventRemove } from '@react-navigation/native'
import { useNavigation } from 'expo-router'
import { isNil } from 'lodash-es'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyboardAvoidingView, Platform, ScrollView } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import AIAvatar from '../aiConnection/AIAvatar'
import AIConnection, { AIConnectionRef } from '../aiConnection/AIConnection'
import AICreateFormEventsHandler from './AICreateFormEventsHandler'
import AIGeneratedFormPreview, { AIGeneratedFormPreviewRef } from './AIGeneratedFormPreview'
import AIPointsAlert from './AIPointsAlert'
import Conversation from './Conversation'
import UserInputBox from './UserInputBox'
import {
  FORM_GENERATION_EXTRA_MESSAGE_PARAMS,
  FORM_GENERATION_UTM_PARAMS,
} from './createFormGenerationEventsHandlerFactory'
import { FormCustomEvents, FormGenerationClientCommand, FormStepEvents } from './types'

type AICreateFormProps = {
  conversationId: string
}

export default function AICreateForm({ conversationId }: AICreateFormProps) {
  const { t } = useTranslation('aiChat')
  const dispatch = useAppDispatch()
  const { bottom } = useSafeAreaInsets()
  const headerHeightReal = useHeaderHeight()
  const androidHeaderHeight = useRef(headerHeightReal)
  // https://github.com/react-navigation/react-navigation/issues/12692
  const headerHeight = Platform.OS === 'android' ? androidHeaderHeight.current : headerHeightReal

  const aiConnectionRef = useRef<AIConnectionRef<FormStepEvents, FormCustomEvents, FormGenerationClientCommand>>(null)

  const aiPoints = useAppSelector(state => state.usageAndBalance.currentMonthlyUsage.aiPointsUsage.totalBalance)
  const aiPointsInsufficient = isNil(aiPoints) ? false : aiPoints <= 0

  const conversationStarted = useAppSelector(state => selectConversationStarted(state, conversationId))
  const navigatedAway = useAppSelector(state => state.formAIGeneration.navigatedAway)

  const { isKeyboardVisible } = useKeyboardState()

  const navigation = useNavigation()

  usePreventRemove(conversationStarted && !navigatedAway, ({ data }) => {
    Alert.alert('你有未保存的内容，确定要离开吗？', undefined, [
      { text: '取消', style: 'cancel' },
      {
        text: '确定',
        style: 'destructive',
        onPress: () => navigation.dispatch(data.action),
      },
    ])
  })

  usePreventLeaveWeb(conversationStarted && !navigatedAway, '你有未保存的内容，确定要离开吗？')

  useEffect(() => {
    dispatch(setNavigatedAway(false))
  }, [dispatch])

  return (
    <KeyboardAvoidingView
      className={Platform.select({ web: 'h-full' })}
      behavior="padding"
      keyboardVerticalOffset={Platform.select({ ios: 0, android: headerHeight })}
    >
      <VStack className="h-full" style={{ paddingBottom: Math.max(isKeyboardVisible ? 0 : bottom, 16) }}>
        {conversationStarted ? (
          <Conversation conversationId={conversationId} paddingTop={headerHeight} />
        ) : (
          <Heading paddingTop={Platform.select({ ios: headerHeight, android: 0 })} />
        )}

        {!isKeyboardVisible && !conversationStarted && <PromptExamples conversationId={conversationId} />}
        <AIPointsAlert points={aiPoints} />
        <UserInputBox conversationId={conversationId} disabled={aiPointsInsufficient} />
        <Text className="text-typography-500 mx-auto mt-2" size="footnote">
          {t('agcWarning')}
        </Text>

        <AutoOpenPreviewAfterFormGenerated conversationId={conversationId} />
        <AIConnection
          ref={aiConnectionRef}
          conversationId={conversationId}
          channelName="AgentFormChannel"
          utmParams={FORM_GENERATION_UTM_PARAMS}
          extraMessageParams={FORM_GENERATION_EXTRA_MESSAGE_PARAMS}
        />
        <AICreateFormEventsHandler conversationId={conversationId} aiConnectionRef={aiConnectionRef} />
      </VStack>
    </KeyboardAvoidingView>
  )
}

function AutoOpenPreviewAfterFormGenerated({ conversationId }: { conversationId: string }) {
  const previewRef = useRef<AIGeneratedFormPreviewRef>(null)
  const previewTriggeredRef = useRef<Record<string, boolean>>({})

  const latestGeneratedForm = useAppSelector(state => selectLatestGeneratedForm(state, conversationId))

  useEffect(() => {
    if (!latestGeneratedForm?.id || !latestGeneratedForm.done || latestGeneratedForm.interrupted) return
    if (previewTriggeredRef.current[latestGeneratedForm.id]) return

    previewTriggeredRef.current[latestGeneratedForm.id] = true
    previewRef.current?.preview()
  }, [latestGeneratedForm])

  if (!latestGeneratedForm || !latestGeneratedForm.done) return null

  return <AIGeneratedFormPreview ref={previewRef} conversationId={conversationId} generatedForm={latestGeneratedForm} />
}

function Heading({ paddingTop }: { paddingTop?: number }) {
  return (
    <VStack className="flex-1 items-center justify-center px-4" space="xs" style={{ paddingTop }}>
      <AIAvatar className="w-24" active />
      <Text className="text-2xl text-typography-900 mt-4">AI 智能创建</Text>
      <Text className="text-lg text-typography-500">输入想法，即可创建出符合需求的表单</Text>
    </VStack>
  )
}

const promptExamples = [
  {
    title: '市集摊主报名',
    prompt: '10 月 5 日文创市集摊主报名表，需要摊主信息、售卖物品/品类、是否用电等信息。名额 50 个，先到先得',
  },
  {
    title: '课程满意度调研',
    prompt: '儿童阅读体验课的满意度调研，面向家长调研，了解满意度和期望，需要 10 道题',
  },
  {
    title: '城市知识测验',
    prompt: '出一份城市西安的在线知识考题，趣味一点，活泼一点。',
  },
]

type PromptExamplesProps = {
  conversationId: string
}

function PromptExamples({ conversationId }: PromptExamplesProps) {
  const dispatch = useAppDispatch()

  const handleSelectPrompt = (prompt: string) => {
    dispatch(setUserInputContent({ conversationId, content: prompt }))
  }

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
      <HStack space="sm" className="px-4 pb-3">
        {promptExamples.map(({ title, prompt }) => (
          <Pressable
            key={title}
            className="flex-shrink-0 bg-background-50 data-[active=true]:bg-background-100 rounded-lg px-5 py-3 outline-none"
            onPress={() => handleSelectPrompt(prompt)}
          >
            <Text className="text-typography-900">{title}</Text>
          </Pressable>
        ))}
      </HStack>
    </ScrollView>
  )
}
